/**
 * Design constants for Scope's two surfaces.
 *
 * Scope wears both themes at once, the same way Review does and for the same
 * reason. The designer is in a tool, alongside Compose and Cloud, and gets the
 * dark app ground. The SME is a guest who has been sent a link — often someone
 * who has never heard of Mauvely — and gets a light page that reads as a
 * conversation rather than as software. So `data-theme` is set per surface in
 * App.tsx, not by a preference, and there is no toggle.
 *
 * Everything below is `var(--*)` from the design system wherever the token set
 * has an answer. The literals are the handful of values the mockup hard-coded
 * outside it.
 */

import type React from 'react';

export const EASE = 'cubic-bezier(.22,.61,.36,1)';

/**
 * App chrome, decided 2026-09-09 — the readme's § "App chrome" section.
 *
 * The window ground is violet-tinted and every content region is a *neutral*
 * panel at 16px radius on it, with 16px of ground showing between and around.
 * Cards on a panel stay `--surface-card`.
 *
 * **Render from the `var()` names, not from these numbers.** The literals are
 * here so the decision is readable in TypeScript and so a value can be checked
 * without opening the token file — they mirror the `--app-*` block at the end
 * of each theme in `web/public/_ds/mauvely/tokens/colors.css`, which is the
 * only definition the browser sees. Nothing here switches on `data-theme`, so
 * a hardcoded hex will not follow the surface.
 */
export const APP = {
  light: {
    ground: '#e8e5f9',
    chromePanel: '#f5f3ff',
    /** slate-50 */
    panel: '#f8f9fd',
    /** slate-100 — strips inside a panel: a toolbar, a tab strip. */
    panelAlt: '#f3f5fe',
    /** Inputs, still reading as inset now the panel under them is lighter. */
    panelSunken: '#eef0f8'
  },
  dark: {
    /** indigo-950 */
    ground: '#12142e',
    chromePanel: '#1b1a33',
    panel: '#1b1d2b',
    panelAlt: '#1f2131',
    panelSunken: '#161826'
  }
} as const;

/** Chrome metrics, from the mockup's inline styles. */
export const LAYOUT = {
  /** The designer's sticky app bar. */
  topbar: 60,
  /** Page column, the design system's `--container-max`. */
  page: 1200,
  /** The live-brief rail beside the chat. */
  rail: 392,
  /** The ground showing between panels and around them — `--gap-grid`, and the
   *  same 16 the native apps use. */
  gutter: 16
} as const;

export const RADIUS = {
  panel: 22,
  card: 16,
  control: 12,
  chip: 10,
  pill: 999,
  /** `--radius-xl`. A chrome panel on the app ground. */
  appPanel: 16
} as const;

/**
 * Motion, from the readme's § Motion. Milliseconds, matching `motion.css`.
 *
 * Anything expressible in CSS is: `--transition-control`, `.mv-page` and
 * `.mv-pop` in `styles.css` carry the shapes and `prefers-reduced-motion` is
 * handled there in one place. These are for the few transitions that have to
 * be inline.
 */
export const DUR = {
  /** Hover and press colour. */
  fast: 120,
  /** Reveals and tab switches. */
  base: 180,
  /** Dialogs and drawers. */
  slow: 280
} as const;

export const SHADOW = {
  hairline: '0 1px 0 rgba(40,28,45,.04)',
  popover: '0 18px 44px rgba(40,28,45,.18)'
} as const;

/** Baloo 2 800. Titles in the Mauvely voice end in a full stop. */
export const display = (size: number): React.CSSProperties => ({
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 800,
  fontSize: size,
  lineHeight: size >= 32 ? 1.02 : 1.1,
  letterSpacing: '-.005em',
  color: 'var(--text-strong)',
  margin: 0
});

/**
 * The brand's signature move: the display family dropped to regular weight
 * instead of switching to the body face. Subtitles and lead-ins, 22–28px.
 */
export const displayLead = (size: number): React.CSSProperties => ({
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 400,
  fontSize: size,
  lineHeight: 1.28,
  color: 'var(--text-body)',
  margin: 0
});

/** Eyebrow labels: mono, wide tracking, uppercase. */
export const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10.5,
  letterSpacing: '.17em',
  textTransform: 'uppercase',
  color: 'var(--text-faint)'
};

/** The smaller eyebrow the mockup uses inside panels. */
export const monoSmall: React.CSSProperties = {
  ...mono,
  fontSize: 9,
  letterSpacing: '.18em'
};

/** Metadata lines: mono, joined with `·`, never commas and never emoji. */
export const monoPlain: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 11,
  letterSpacing: '.06em',
  color: 'var(--text-faint)'
};

/** A panel: white on the light surface, surface-card on the dark one. */
export const panel: React.CSSProperties = {
  background: 'var(--surface-card)',
  borderRadius: RADIUS.panel,
  boxShadow: SHADOW.hairline
};

/** The entrance every arriving block uses. */
export const entrance = `mvIn .3s ${EASE}`;

// ── origins ─────────────────────────────────────────────────────────────────

/**
 * Where the account lives.
 *
 * Copied from Cloud's `accountOrigin()`, localhost arm included: main runs on
 * :8787 in development and a hardcoded production origin would make the
 * sign-in round trip impossible to exercise locally.
 */
export function accountOrigin(): string {
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:8787`;
  return 'https://mauvely.com';
}

/** Round-trips through the main site, which owns sessions. It lands back here
 *  because `scope.mauvely.com` is in `RETURN_HOSTS` in website-main's
 *  shared/returnto.ts — without that entry this ends on /account instead. */
export const signInHref = (returnTo: string): string =>
  `${accountOrigin()}/signin?next=${encodeURIComponent(returnTo)}`;

// ── formatting ──────────────────────────────────────────────────────────────

/**
 * Relative for the last week, then an absolute short date.
 *
 * Copied from the sibling apps' theme.ts rather than imported, because the web
 * apps share no package. If you change one, change the others.
 */
export function formatWhen(unixSeconds: number): string {
  const then = new Date(unixSeconds * 1000);
  const diff = Date.now() / 1000 - unixSeconds;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 172800) return 'yesterday';
  if (diff < 604800) return then.toLocaleDateString('en-GB', { weekday: 'long' });
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** A meeting time, rendered in the reader's own locale. */
export function formatMeeting(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** A submitted date: "6 Aug". */
export const formatDate = (unixSeconds: number): string =>
  new Date(unixSeconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** "AR" for "Ada Rowe", falling back to the address. */
export function initials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1 && parts[0]!.length >= 2) return parts[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

/** First name only, for the places the product speaks about a person. */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** The five brief sections, in display order. Mirrors worker/lib/ai/prompts.ts —
 *  the labels are copy and live on both sides of the wire. */
export const SECTIONS = [
  { key: 'problem', label: 'The problem' },
  { key: 'audience', label: "Who it's for" },
  { key: 'success', label: 'What success looks like' },
  { key: 'existing', label: 'What already exists' },
  { key: 'constraints', label: 'Constraints' }
] as const;

export type SectionKey = (typeof SECTIONS)[number]['key'];

/** Flag tags, as the designer's view labels them. */
export const FLAG_LABEL: Record<string, string> = {
  opportunity: 'Opportunity',
  constraint: 'Constraint',
  raised: 'Raised'
};
